import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from '@material-ui/core';
import { AndromedaJobsTable } from './JobsTable';
import { useAndromeda } from '../../../modules/Andromeda';
import { AppContext } from '../../../modules/AppContext';
import { AndromedaJobsFilters } from './Filters';
import { SignalRHubGroupListener } from '../../../components/SignalRHubGroupListener';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
}));

const getInitialQueryParams = () => {
  const mountParams = new URLSearchParams(window.location.search);
  return {
    participantId: mountParams.get('participantId') ?? '',
    agent: mountParams.get('agent') ?? 'all',
    type: mountParams.get('type') ?? 'all',
  };
};

export const AndromedaJobsExplorer = () => {
  const classes = useStyles();
  const history = useHistory();
  const { showToast } = useContext(AppContext);
  const {
    getJobs,
    signalRConfig: {
      hubUrl,
      hubName,
      hubGroups: { jobUpdates: hubGroup },
    },
  } = useAndromeda();

  const [inProgressJobs, setInProgressJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFilters, setFilters] = useState(getInitialQueryParams());
  const [appliedFilters, setAppliedFilters] = useState(currentFilters);
  const appliedFiltersRef = useRef(currentFilters);

  const listeners = [
    {
      name: hubGroup.listenerMethods.updateJob,
      method: (newJob) => {
        const adheresToCurrentFilters =
          (appliedFiltersRef.current.agent === 'all' ||
            appliedFiltersRef.current.agent === newJob.agent) &&
          (appliedFiltersRef.current.participantId === '' ||
            Number(appliedFiltersRef.current.participantId) ===
              newJob.participantId) &&
          (appliedFiltersRef.current.type === 'all' ||
            appliedFiltersRef.current.type === newJob.type);

        if (!adheresToCurrentFilters) return;

        if (newJob.finished !== null) {
          setInProgressJobs((jobs) => jobs.filter((x) => x.id !== newJob.id));
          setCompletedJobs((jobs) => [newJob, ...jobs]);
        } else {
          setInProgressJobs((jobs) => {
            const inProgressWithoutNewJob = jobs.filter(
              (x) => x.id !== newJob.id
            );
            return [newJob, ...inProgressWithoutNewJob];
          });
        }
      },
    },
  ];

  const applyFilters = () => {
    setAppliedFilters(currentFilters);
    appliedFiltersRef.current = currentFilters;
  };

  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedFilters.participantId)
      params.set('participantId', appliedFilters.participantId);
    if (appliedFilters.agent && appliedFilters.agent !== 'all')
      params.set('agent', appliedFilters.agent);
    if (appliedFilters.type && appliedFilters.type !== 'all')
      params.set('type', appliedFilters.type);

    return params.toString();
  }, [appliedFilters]);

  const fetchWithFilters = useCallback(async () => {
    setLoading(true);

    history.push(
      `${window.location.pathname}${
        filterParams.length === 0 ? '' : `?${filterParams}`
      }`
    );

    const jobs = await getJobs(filterParams);
    if (jobs) {
      setInProgressJobs(jobs.inProgress);
      setCompletedJobs(jobs.recentlyCompleted);
    } else {
      showToast({
        type: 'error',
        message: 'Failed to fetch jobs',
      });
    }
    setLoading(false);
  }, [filterParams, getJobs, history, showToast]);

  useEffect(() => {
    fetchWithFilters();
  }, [fetchWithFilters]);

  return (
    <>
      <SignalRHubGroupListener
        hubUrl={hubUrl}
        hubName={hubName}
        hubGroup={hubGroup}
        hubListeners={listeners}
      />
      <section className={classes.wrapper}>
        <AndromedaJobsFilters
          loading={loading}
          applyFilters={applyFilters}
          filters={currentFilters}
          setFilters={setFilters}
        />
        <AndromedaJobsTable
          data={inProgressJobs}
          loading={loading}
          title="Active Andromeda Jobs"
        />
        <AndromedaJobsTable
          data={completedJobs}
          loading={loading}
          title="Recent Andromeda Jobs"
        />
      </section>
    </>
  );
};
